import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';
import {
  afterNextRender,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatSuffix, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';

import { MatTooltip } from '@angular/material/tooltip';
import { RootState } from '@next/core-lib/app';
import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';

import { debounceTime, skipWhile, take } from 'rxjs/operators';

import {
  DosatorConversions,
  DosatorProduct,
  DosatorSetting,
  EnergySystem,
  LysineSystem,
} from '../../../settings/models/settings.model';
import {
  getDosatorSettings,
  getEnergySetting,
  getLysineSetting,
} from '../../../settings/store/selectors/settings.selectors';
import { Product } from '../../../shared/models/shared.model';

import {
  CalculationConsumptionResult,
  CalculationDirection,
  CalculationDosatorSettingsResult,
  CalculationPhaseResult,
  Consumption,
  DosatorSettingsConversionLimits,
  FeedAdviceLine,
  FeedAdviceProperties,
  FeedAdviceType,
  FeedingType,
  InputSystemType,
  Phase,
  PhaseTransitionTypes,
  SchemeTableRow,
  TableScheme,
  ValidationError,
} from '../../models/feed-advice.model';
import { SchemeSupportService } from '../../services/scheme-support.service';
import {
  getShowDosatorSetting,
  getShowEnergySetting,
  getShowLysineSetting,
} from '../../store/feed-advice.selectors';

@Component({
  selector: 'app-feed-advice-table',
  templateUrl: './feed-advice-table.component.html',
  styleUrls: ['./feed-advice-table.component.scss'],
  imports: [
    NgIf,
    MatButton,
    MatIcon,
    MatTable,
    ReactiveFormsModule,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFormField,
    NgClass,
    MatInput,
    AgrNumberDirective,
    MatTooltip,
    MatSuffix,
    MatSelect,
    MatOption,
    NgFor,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatError,
    AsyncPipe,
    TranslatePipe,
    AgrNumberPipe,
  ],
})
export class FeedAdviceTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isEditMode: boolean;
  @Input() showLysineColumn: boolean;
  @Input() showEnergyColumn: boolean;
  @Input() showDosatorColumn: boolean;
  @Input() feedAdviceType: FeedAdviceType | string;
  @Input() isReferenceFeedAdvice: boolean;
  @Input() tableScheme: TableScheme;
  @Input() products: Product[];
  @Input() finalWeight: number;
  @Input() feedingType?: FeedingType;

  @Output() updateSummaryTable = new EventEmitter<void>();

  energySetting$: Observable<EnergySystem> = this.store.select(getEnergySetting);
  lysineSetting$: Observable<LysineSystem> = this.store.select(getLysineSetting);

  changeNumberOfLinesClickStream$ = new Subject<string>();
  dosatorSettings$: Observable<DosatorSetting[]> = this.store.select(getDosatorSettings);

  readonly ALLOWED_LYSINE_DEVIATION = 0.05;
  linesWithValidationErrors: ValidationError[] = [];
  tableRows: SchemeTableRow[];
  readonly datasource = new MatTableDataSource<SchemeTableRow>([]);
  phases: Phase[];
  numberOfPhases: number;
  phaseTransition: PhaseTransitionTypes;
  productsPhase1: Product[];
  productsPhase2: Product[];
  productsPhase3: Product[];
  productsPhase4: Product[];
  inputSystem: InputSystemType = InputSystemType.ENERGYDAY;
  pigletsWeaningAge: number;
  rearingAge: number;
  selectedDosatorSettingsId: number;
  selectedDosatorSettings: DosatorSetting;
  calculateColumnsValidationError: string;
  tableRowsObs: Observable<SchemeTableRow>;

  columns = [
    'week',
    'day',
    'weekAge',
    'age',
    'weight',
    'energyValue',
    'lysine',
    'correctionFactor',
    'phase1',
    'phase2',
    'phase3',
    'phase4',
    'dosator_feeding_times',
    'dosator_position_phase_1',
    'dosator_position_phase_2',
    'dosator_position_phase_3',
    'dosator_position_phase_4',
    'foodPerDay',
    'cumulative',
  ];
  displayedColumns: Array<string> = [];
  productsColumns = [
    'invisible1',
    'invisible2',
    'invisible3',
    'invisible4',
    'invisible5',
    'invisible6',
    'invisible7',
    'invisible8',
    'productPhase1',
    'productPhase2',
    'productPhase3',
    'productPhase4',
    'productdosator_feeding_times',
    'productdosator_position_phase_1',
    'productdosator_position_phase_2',
    'productdosator_position_phase_3',
    'productdosator_position_phase_4',
    'invisible9',
    'invisible10',
  ];
  displayedProductsColumns: Array<string> = [];

  formArray: FormArray;
  formGroup: FormGroup;
  finalBodyWeightFormGroup: FormGroup;
  subscriptions: Subscription[][] = [];

  constructor(
    private readonly schemeSupportService: SchemeSupportService,
    private readonly formBuilder: FormBuilder,
    private readonly store: Store<RootState>,
    private readonly locale: LocaleService,
    private readonly injector: Injector,
  ) {
    this.changeNumberOfLinesClickStream$.pipe(debounceTime(200)).subscribe((operation) => {
      this.recalculateNumberOfLines(operation);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (Object.prototype.hasOwnProperty.call(changes, 'products') && this.products) {
      this.productsPhase1 = this.getProductsForPhase(1);
      this.productsPhase2 = this.getProductsForPhase(2);
      this.productsPhase3 = this.getProductsForPhase(3);
      this.productsPhase4 = this.getProductsForPhase(4);
    }

    if (Object.prototype.hasOwnProperty.call(changes, 'feedingType') && this.tableRows) {
      this.updateTableRows();
    }
  }

  ngOnInit() {
    if (this.tableScheme) {
      this.tableScheme = {
        ...this.tableScheme,
        feedAdviceLines: [
          ...this.tableScheme.feedAdviceLines.map((line) => ({
            ...line,
            correctionFactor:
              !Object.prototype.hasOwnProperty.call(line, 'correctionFactor') ||
              !line.correctionFactor
                ? line.week < 15 || this.isWeanedPigletsFeedadvice() || this.isRearingFeedadvice()
                  ? 1.0
                  : 0.9
                : line.correctionFactor,
          })),
        ],
      };
      this.formArray = this.formBuilder.array(
        this.tableScheme.feedAdviceLines.map((line) => this.createFormControl(line)),
      );
      this.formGroup = this.formBuilder.group({ lines: this.formArray });

      this.finalBodyWeightFormGroup = new FormGroup({
        finalWeight: new FormControl(this.finalWeight),
      });

      // hold these apart from tableScheme, any added or deleted phases will be updated in these variables.
      this.phases = this.tableScheme.phases;
      this.numberOfPhases = this.tableScheme.numberOfPhases;
      this.phaseTransition = this.tableScheme.phaseTransition;
      this.inputSystem = this.tableScheme.inputSystem ?? InputSystemType.ENERGYDAY;
      this.pigletsWeaningAge = this.tableScheme.pigletWeaningAge ?? 0;
      this.rearingAge = this.tableScheme.rearingAge ?? 0;

      if (this.products) {
        this.productsPhase1 = this.getProductsForPhase(1);
        this.productsPhase2 = this.getProductsForPhase(2);
        this.productsPhase3 = this.getProductsForPhase(3);
        this.productsPhase4 = this.getProductsForPhase(4);
      }

      // create the table
      this.setupColumns(this.numberOfPhases, this.phaseTransition, this.inputSystem);

      this.setTableRows(
        this.schemeSupportService.calculateTableRows(
          this.tableScheme,
          this.inputSystem,
          this.feedingType,
        ),
      );
    }
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscriptionList) =>
        subscriptionList.forEach((s) => {
          if (s) {
            s.unsubscribe();
          }
        }),
      );
    }
  }

  getProductsForPhase(phaseNumber: number) {
    return this.products
      .filter((x) => x.suitablePhases.find((p) => p === phaseNumber))
      .filter((x) => x.schemeTypes.find((p) => p === this.feedAdviceType))
      .sort((a, b) => (a.code > b.code ? 1 : -1));
  }

  isWeanedPigletsFeedadvice(): boolean {
    return this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS;
  }

  private setTableRows(rows: SchemeTableRow[]) {
    this.tableRows = rows;
    this.datasource.data = [...rows];
  }

  private refreshTableRows() {
    this.datasource.data = [...this.tableRows];
  }

  isRearingFeedadvice(): boolean {
    return this.feedAdviceType === FeedAdviceType.REARING;
  }

  setupColumns(
    numberOfPhases: number,
    phaseTransition: PhaseTransitionTypes,
    inputSystem: InputSystemType,
  ) {
    const phase2String = 'phase2';
    const phase3String = 'phase3';
    const phase4String = 'phase4';
    const dosator2string = 'dosator_position_phase_2';
    const dosator3string = 'dosator_position_phase_3';
    const dosator4string = 'dosator_position_phase_4';
    const updatePhasesAndLinesInScheme = () => {
      let newNrOfPhases: number;
      if (numberOfPhases > this.phases.length) {
        newNrOfPhases = this.phases.length + 1;
        this.numberOfPhases = newNrOfPhases;
        this.phaseTransition = phaseTransition;
        this.phases = [
          ...this.phases,
          {
            phaseNumber: newNrOfPhases,
            product: null,
          },
        ];
        this.setTableRows(
          this.tableRows.map((line) => ({
            ...line,
            consumptions: [
              ...line.consumptions,
              {
                phaseNumber: newNrOfPhases,
              },
            ],
          })),
        );
      } else if (numberOfPhases < this.phases.length) {
        newNrOfPhases = this.phases.length - 1;
        this.numberOfPhases = newNrOfPhases;
        this.phaseTransition = phaseTransition;
        this.phases = <Phase[]>this.phases.filter((p) => p.phaseNumber <= newNrOfPhases);
        this.setTableRows(
          this.tableRows.map((line) => ({
            ...line,
            consumptions: <Consumption[]>(
              line.consumptions.filter((f) => f.phaseNumber <= newNrOfPhases)
            ),
          })),
        );
      } else if (inputSystem !== this.inputSystem) {
        this.inputSystem = inputSystem;
        // The form controls contain a reference to an old reference to the tableRows.
        // The tablerows contain the 'new truth'. We therefore have to patch the controls explicitly.
        this.tableRows.forEach((tr) => {
          const correspondingFormGroup = this.formArray.controls.find(
            (fg) => fg.value.lineNr === tr.line,
          );
          if (correspondingFormGroup) {
            correspondingFormGroup.patchValue({
              ...correspondingFormGroup.value,
              energyValue: tr.energyValuePerDay,
              foodPerDay: tr.foodPerDay,
            });
          }
        });
      } else {
        this.phaseTransition = phaseTransition;
      }
      if (
        numberOfPhases !== this.numberOfPhases ||
        phaseTransition !== this.phaseTransition ||
        inputSystem !== this.inputSystem
      ) {
        updatePhasesAndLinesInScheme();
      }
    };

    switch (numberOfPhases) {
      case 1:
        this.displayedColumns = this.columns.filter(
          (column) =>
            !column.toLowerCase().includes(phase2String) &&
            !column.toLowerCase().includes(phase3String) &&
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator2string) &&
            !column.toLowerCase().includes(dosator3string) &&
            !column.toLowerCase().includes(dosator4string),
        );

        this.displayedProductsColumns = this.productsColumns.filter(
          (column) =>
            !column.toLowerCase().includes(phase2String) &&
            !column.toLowerCase().includes(phase3String) &&
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator2string) &&
            !column.toLowerCase().includes(dosator3string) &&
            !column.toLowerCase().includes(dosator4string),
        );

        break;

      case 2:
        this.displayedColumns = this.columns.filter(
          (column) =>
            !column.toLowerCase().includes(phase3String) &&
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator3string) &&
            !column.toLowerCase().includes(dosator4string),
        );

        this.displayedProductsColumns = this.productsColumns.filter(
          (column) =>
            !column.toLowerCase().includes(phase3String) &&
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator3string) &&
            !column.toLowerCase().includes(dosator4string),
        );

        break;

      case 3:
        this.displayedColumns = this.columns.filter(
          (column) =>
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator4string),
        );

        this.displayedProductsColumns = this.productsColumns.filter(
          (column) =>
            !column.toLowerCase().includes(phase4String) &&
            !column.toLowerCase().includes(dosator4string),
        );

        break;

      case 4:
        this.displayedColumns = this.columns;
        this.displayedProductsColumns = this.productsColumns;
        break;
    }

    // only show column 'correctionFactor' for fattening pigs.
    if (this.tableScheme.feedAdviceType !== FeedAdviceType.FATTENING) {
      this.displayedColumns = this.displayedColumns.filter(
        (column) => !column.includes('correctionFactor'),
      );
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible8'),
      );
    }

    // do not show 'weekAge'-column for others than REARING
    if (this.tableScheme.feedAdviceType !== FeedAdviceType.REARING) {
      this.displayedColumns = this.displayedColumns.filter((column) => column !== 'weekAge');
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible3'),
      );
    }

    // do not show 'day'-column for REARING
    if (this.tableScheme.feedAdviceType === FeedAdviceType.REARING) {
      this.displayedColumns = this.displayedColumns.filter((column) => !column.includes('day'));
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible2'),
      );
    }

    // WEANED_PIGLETS: do not show 'week'-column...
    if (
      this.tableScheme.feedAdviceType === FeedAdviceType.WEANED_PIGLETS ||
      this.tableScheme.feedAdviceType === FeedAdviceType.REARING
    ) {
      this.displayedColumns = this.displayedColumns.filter((column) => column !== 'week');
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => column.toLowerCase() !== 'invisible1',
      );
    } else {
      // ... else do not show 'age'-column for FATTENING
      this.displayedColumns = this.displayedColumns.filter((column) => !column.includes('age'));
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible4'),
      );
    }

    // SHOW LYSINE COLUMN: do not show if that setting is false
    const filterLysineColumns = () => {
      this.displayedColumns = this.displayedColumns.filter((column) => !column.includes('lysine'));
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible7'),
      );
    };
    if (this.showLysineColumn === undefined) {
      // if it is not set to true or false, retrieve it from state...
      this.store
        .select(getShowLysineSetting)
        .pipe(skipWhile((showLysine) => showLysine === null))
        .subscribe((showLysine) => {
          if (!showLysine) {
            filterLysineColumns();
          }
        });
      // ...else use it from the passed in properties
    } else if (!this.showLysineColumn) {
      filterLysineColumns();
    }

    // SHOW ENERGY COLUMN: do not show if that setting is false
    const filterEnergyColumns = () => {
      this.displayedColumns = this.displayedColumns.filter((column) => !column.includes('energy'));
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('invisible6'),
      );
    };
    if (this.showEnergyColumn === undefined) {
      // if it is not set to true or false, retrieve it from state...
      this.store
        .select(getShowEnergySetting)
        .pipe(skipWhile((showEnergy) => showEnergy === null))
        .subscribe((showEnergy) => {
          if (!showEnergy) {
            filterEnergyColumns();
          }
        });
      // ...else use it from the passed in properties
    } else if (!this.showEnergyColumn) {
      filterEnergyColumns();
    }

    // SHOW DOSATOR COLUMN: do not show if that setting is false
    const filterDosatorColumns = () => {
      this.displayedColumns = this.displayedColumns.filter(
        (column) => !column.includes('dosator_feeding_times'),
      );
      this.displayedColumns = this.displayedColumns.filter(
        (column) => !column.includes('dosator_position_phase_'),
      );
      this.displayedProductsColumns = this.displayedProductsColumns.filter(
        (column) => !column.toLowerCase().includes('productdosator_position_phase_'),
      );
    };
    if (
      this.tableScheme.feedAdviceType === FeedAdviceType.REARING &&
      this.showDosatorColumn === undefined
    ) {
      // if it is not set to true or false, retrieve it from state...
      this.store
        .select(getShowDosatorSetting)
        .pipe(skipWhile((showDosator) => showDosator === null))
        .subscribe((showDosator) => {
          if (this.isEditMode && !showDosator) {
            filterDosatorColumns();
          }
        });
      // ...else use it from the passed in properties
    } else if (
      this.tableScheme.feedAdviceType !== FeedAdviceType.REARING ||
      !this.showDosatorColumn
    ) {
      filterDosatorColumns();
    }

    if (
      numberOfPhases !== this.phases.length ||
      phaseTransition !== this.phaseTransition ||
      inputSystem !== this.inputSystem
    ) {
      updatePhasesAndLinesInScheme();
    }
  }

  private createFormControl(line: FeedAdviceLine): FormGroup {
    const lineSubscriptions: Subscription[] = [];
    const percentageValidator = [Validators.min(0), Validators.max(100)];

    const lineNr = new FormControl(this.isWeanedPigletsFeedadvice() ? line.day : line.week);
    const age = new FormControl(
      this.isWeanedPigletsFeedadvice() || this.isRearingFeedadvice() ? line.age : null,
    );
    const phase1 = new FormControl(
      line.consumptions[0]?.productIntakeInPercentage,
      percentageValidator,
    );
    const phase2 = new FormControl(
      line.consumptions[1]?.productIntakeInPercentage,
      percentageValidator,
    );
    const phase3 = new FormControl(
      line.consumptions[2]?.productIntakeInPercentage,
      percentageValidator,
    );
    const phase4 = new FormControl(
      line.consumptions[3]?.productIntakeInPercentage,
      percentageValidator,
    );
    const energyValue = new FormControl(line.energyValuePerDay, [
      Validators.required,
      Validators.min(0),
    ]);

    const dosator_feeding_times = new FormControl(line.feedingTimesPerDay);

    const dosator_position_phase_1 = new FormControl(line.consumptions[0]?.dosatorPosition);

    const dosator_position_phase_2 = new FormControl(line.consumptions[1]?.dosatorPosition);

    const dosator_position_phase_3 = new FormControl(line.consumptions[2]?.dosatorPosition);

    const dosator_position_phase_4 = new FormControl(line.consumptions[3]?.dosatorPosition);

    const foodPerDay = new FormControl(line.foodPerDay, [Validators.required, Validators.min(0)]);
    const weight = new FormControl(line.weight, [Validators.required, Validators.min(0)]);
    const correctionFactor = new FormControl(line.correctionFactor, [
      Validators.required,
      Validators.min(0),
    ]);

    const formGroup = new FormGroup({
      lineNr,
      age,
      phase1,
      phase2,
      phase3,
      phase4,
      dosator_feeding_times,
      dosator_position_phase_1,
      dosator_position_phase_2,
      dosator_position_phase_3,
      dosator_position_phase_4,
      energyValue,
      foodPerDay,
      weight,
      correctionFactor,
    });

    const getTableRow = () =>
      this.tableRows.find((tableRow) => tableRow.line === formGroup.controls.lineNr.value);

    const updateTableRow =
      (type: string, phaseNumber?: number) => (value: number | null | undefined) => {
        const tableRow = getTableRow();
        if (!tableRow || value === undefined) {
          return;
        }

        this.setValue(tableRow, type, value as number, phaseNumber);
      };

    lineSubscriptions.push(
      phase1.valueChanges.subscribe(updateTableRow('percentage', 1)),
      phase2.valueChanges.subscribe(updateTableRow('percentage', 2)),
      phase3.valueChanges.subscribe(updateTableRow('percentage', 3)),
      phase4.valueChanges.subscribe(updateTableRow('percentage', 4)),
      dosator_feeding_times.valueChanges.subscribe(updateTableRow('feedingTimesPerDay')),
      dosator_position_phase_1.valueChanges.subscribe(updateTableRow('dosator', 1)),
      dosator_position_phase_2.valueChanges.subscribe(updateTableRow('dosator', 2)),
      dosator_position_phase_3.valueChanges.subscribe(updateTableRow('dosator', 3)),
      dosator_position_phase_4.valueChanges.subscribe(updateTableRow('dosator', 4)),
      energyValue.valueChanges.subscribe(updateTableRow('energyValue')),
      foodPerDay.valueChanges.subscribe(updateTableRow('foodPerDay')),
      weight.valueChanges.subscribe(updateTableRow('weight')),
      correctionFactor.valueChanges.subscribe(updateTableRow('correctionFactor')),
    );
    this.subscriptions.push(lineSubscriptions);

    return formGroup;
  }

  validateTable() {
    this.linesWithValidationErrors = <ValidationError[]>[
      ...this.tableRows
        .filter((line) => line.energyValuePerDay === null || line.energyValuePerDay <= 0)
        .map((line) => ({ line: line.line, type: 'energyValue' })),
      ...this.tableRows
        .filter((line) => line.foodPerDay === null || line.foodPerDay <= 0)
        .map((line) => ({ line: line.line, type: 'foodPerDay' })),
      ...this.tableRows
        .filter((line) => line.weight === null || line.weight <= 0)
        .map((line) => ({ line: line.line, type: 'weight' })),
      ...this.tableRows
        .filter((line) => line.correctionFactor <= 0 || line.correctionFactor > 1)
        .map((line) => ({ line: line.line, type: 'correctionFactor' })),
      ...this.tableRows
        .filter((line) => this.validateTableRowPercentages(line))
        .map((line) => ({ line: line.line, type: 'percentage' })),
    ];
  }

  validateTableRowPercentages(line: SchemeTableRow): boolean {
    let total = 0;

    line.consumptions.map((c) =>
      c?.productIntakeInPercentage !== undefined ? (total += c?.productIntakeInPercentage) : 0,
    );

    return total !== 100;
  }

  /** UPDATE SCHEMETABLE ----------------------------------------------------------------------------
   * User sets value of weight, energy, lysine or one of the phases in the table.
   **/
  setValue(tableRow: SchemeTableRow, type: string, value: number, phaseNumber?: number) {
    const updateLinesWithValidationErrors = (line: number, linesFailingValidation: number[]) => {
      const validationError = this.linesWithValidationErrors.findIndex(
        (e) => e.line === line && e.type === type,
      );
      if (validationError > -1) {
        this.linesWithValidationErrors.splice(validationError, 1);
      }

      for (const failingLine of linesFailingValidation) {
        if (
          line === failingLine &&
          !this.linesWithValidationErrors.some((e) => e.line === failingLine && e.type === type)
        ) {
          this.linesWithValidationErrors.push(<ValidationError>{ line: failingLine, type });
        }
      }
    };
    switch (type) {
      case 'energyValue':
        tableRow.energyValuePerDay = value;
        this.updateTableRows();

        // validation of energy value: should be larger than 0
        updateLinesWithValidationErrors(
          tableRow.line,
          this.tableRows
            .filter((line) => line.energyValuePerDay === null || line.energyValuePerDay <= 0)
            .map((line) => line.line),
        );
        break;
      case 'foodPerDay':
        tableRow.foodPerDay = value;
        this.updateTableRows();

        // validation of foodPerDay: should be larger than 0
        updateLinesWithValidationErrors(
          tableRow.line,
          this.tableRows
            .filter((line) => line.foodPerDay === null || line.foodPerDay <= 0)
            .map((line) => line.line),
        );
        break;

      case 'weight':
        tableRow.weight = value;
        this.updateTableRows();

        // validation of weight: should be larger than 0
        updateLinesWithValidationErrors(
          tableRow.line,
          this.tableRows
            .filter((line) => line.weight === null || line.weight <= 0)
            .map((line) => line.line),
        );
        break;

      case 'correctionFactor':
        tableRow.correctionFactor = value;
        this.updateTableRows();

        // validation of correction factor: should be between 0 and 1
        if (!this.isWeanedPigletsFeedadvice()) {
          updateLinesWithValidationErrors(
            tableRow.line,
            this.tableRows
              .filter(
                (line) =>
                  line.correctionFactor === null ||
                  line.correctionFactor <= 0 ||
                  line.correctionFactor > 1,
              )
              .map((line) => line.line),
          );
        }
        break;

      case 'percentage':
        tableRow.consumptions = [
          ...tableRow.consumptions.filter((c) => c.phaseNumber !== phaseNumber),
          {
            ...tableRow.consumptions.find((c) => c.phaseNumber === phaseNumber),
            productIntakeInPercentage: value ?? 0,
          },
        ]
          .filter((c) => Object.prototype.hasOwnProperty.call(c, 'phaseNumber'))
          .sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1));

        this.updateTableRows();

        // validation of percentages: the sum must be 100
        updateLinesWithValidationErrors(
          tableRow.line,
          this.tableRows
            .filter((line) =>
              line.consumptions.some((c) =>
                Object.prototype.hasOwnProperty.call(c, 'productIntakeInPercentage'),
              ),
            )
            .filter(
              (line) =>
                line.consumptions.reduce(
                  (total, c) => (total += c.productIntakeInPercentage ?? 0),
                  0,
                ) !== 100,
            )
            .map((line) => line.line),
        );
        break;
      case 'feedingTimesPerDay':
        tableRow.feedingTimesPerDay = value;
        this.updateTableRows();

        break;
      case 'dosator':
        tableRow.consumptions = [
          ...tableRow.consumptions.filter((c) => c.phaseNumber !== phaseNumber),
          {
            ...tableRow.consumptions.find((c) => c.phaseNumber === phaseNumber),
            dosatorPosition: value ?? null,
          },
        ]
          .filter((c) => Object.prototype.hasOwnProperty.call(c, 'phaseNumber'))
          .sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1));

        this.updateTableRows();

        break;
    }
  }

  private updateTableRows() {
    let previousCumulative: number = null;
    this.tableRows.forEach((row) => {
      const line = this.tableRows.find((w) => w.line === row.line);
      const nextLine =
        line.line < this.tableRows.length
          ? this.tableRows.find((w) => w.line === line.line + 1)
          : null;
      row.intestinalDigestibleLysinePerDay = this.schemeSupportService.calculateLysinePerDay(
        line,
        this.phases,
        this.inputSystem,
        this.isWeanedPigletsFeedadvice(),
      );
      if (this.inputSystem === InputSystemType.ENERGYDAY) {
        row.foodPerDay = this.schemeSupportService.calculateFeedPerDay(
          line,
          this.phases,
          this.inputSystem,
          this.isWeanedPigletsFeedadvice(),
        );
      } else {
        row.energyValuePerDay =
          this.schemeSupportService.calculateEnergyValue(
            line,
            this.phases,
            this.isWeanedPigletsFeedadvice(),
          ) / 1;
      }

      if (this.isWeanedPigletsFeedadvice()) {
        row.cumulative =
          this.schemeSupportService.calculateCumulativeForGradualTransitionOrWeanedPiglets(
            this.phases,
            line,
            nextLine,
            previousCumulative,
            this.isWeanedPigletsFeedadvice(),
          );
      } else if (this.isRearingGilt() && this.feedingType === FeedingType.MANUAL) {
        row.cumulative = previousCumulative + 7 * line.foodPerDay;
      } else {
        // FeedAdviceType is FATTENING or REARING
        switch (this.phaseTransition) {
          case PhaseTransitionTypes.ABRUPT:
            row.cumulative = this.schemeSupportService.calculateCumulativeForAbruptTransition(
              line.week,
              this.schemeSupportService.calculateFeedPerDay(
                line,
                this.phases,
                this.inputSystem,
                this.isWeanedPigletsFeedadvice(),
              ),
              line.correctionFactor,
              previousCumulative,
              nextLine != null
                ? this.schemeSupportService.calculateFeedPerDay(
                    nextLine,
                    this.phases,
                    this.inputSystem,
                    this.isWeanedPigletsFeedadvice(),
                  )
                : null,
            );
            break;
          case PhaseTransitionTypes.GRADUAL:
            row.cumulative =
              this.schemeSupportService.calculateCumulativeForGradualTransitionOrWeanedPiglets(
                this.phases,
                line,
                nextLine,
                previousCumulative,
                this.isWeanedPigletsFeedadvice(),
              );
            break;
        }
      }
      line.cumulativeFoodInKg = row.cumulative;
      previousCumulative = row.cumulative;
    });
    this.refreshTableRows();
    afterNextRender(() => this.updateSummaryTable.emit(), {
      injector: this.injector,
    });
  }

  // This method runs when properties are changed
  updateFeedAdviceProperties(properties: FeedAdviceProperties) {
    if (
      properties.phases !== this.numberOfPhases ||
      properties.phaseTransition !== this.phaseTransition ||
      properties.inputSystem !== this.inputSystem ||
      properties.weaningAge !== this.pigletsWeaningAge ||
      properties.rearingAge !== this.rearingAge ||
      properties.showLysine !== this.showLysineColumn ||
      properties.showEnergy !== this.showEnergyColumn ||
      properties.showDosator !== this.showDosatorColumn ||
      properties.selectedDosatorSettingsId !== this.selectedDosatorSettingsId
    ) {
      this.showLysineColumn = properties.showLysine;
      this.showEnergyColumn = properties.showEnergy;
      this.showDosatorColumn = properties.showDosator;
      this.numberOfPhases = properties.phases;
      this.phaseTransition = properties.phaseTransition;
      this.inputSystem = properties.inputSystem;
      this.pigletsWeaningAge = properties.weaningAge;
      this.rearingAge = properties.rearingAge;
      this.selectedDosatorSettingsId = properties.selectedDosatorSettingsId;
      this.setupColumns(properties.phases, properties.phaseTransition, properties.inputSystem);

      if (properties.feedAdviceType === FeedAdviceType.WEANED_PIGLETS) {
        this.setTableRows(
          this.tableRows.map((row) => ({
            ...row,
            age: row.day + (properties.weaningAge ?? 0),
          })),
        );
      } else if (properties.feedAdviceType === FeedAdviceType.REARING) {
        this.setTableRows(
          this.tableRows.map((row, index) => ({
            ...row,
            weekAge: Math.floor(
              ((index + 1) * 7 - 6 + (this.rearingAge ? this.rearingAge - 1 : 0)) / 7,
            ),
            age: (index + 1) * 7 - 6 + (this.rearingAge ? this.rearingAge - 1 : 0),
          })),
        );
      } else {
        // for fattening or rearing feedadvices only
        if (this.phaseTransition === PhaseTransitionTypes.ABRUPT) {
          // any phase percentages that still have values between 0 en 100, must be deleted.
          this.setTableRows(
            this.tableRows.map((row) => ({
              ...row,
              consumptions: row.consumptions
                .map((c) => {
                  if (c.productIntakeInPercentage > 0 && c.productIntakeInPercentage < 100) {
                    this.linesWithValidationErrors.push(<ValidationError>{
                      line: row.line,
                      type: 'percentage',
                    });
                    return <Consumption>{
                      phaseNumber: c.phaseNumber,
                      dosatorPosition: c.dosatorPosition,
                    };
                  }
                  return c;
                })
                .sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1)),
            })),
          );
        }
      }
    }

    this.dosatorSettings$
      .pipe(
        skipWhile((dosatorSettings) => !dosatorSettings),
        take(1),
      )
      .subscribe((dosatorSettings) => {
        this.selectedDosatorSettings = dosatorSettings.find(
          (d) => d.id === this.selectedDosatorSettingsId,
        );
      });
  }

  // TEMPLATE VALIDATION FUNCTIONS --------------------------------------------------------------------------
  checkProductValidation(phasenumber: number) {
    return this.getProductDropdownValue(phasenumber) ? '' : 'product-validation';
  }

  get productsWithValidationErrors(): number[] {
    return Array.from({ length: this.numberOfPhases ?? 0 }, (_, index) => index + 1).filter(
      (phaseNumber) => !this.getProductDropdownValue(phaseNumber),
    );
  }

  highlight(tableRow: SchemeTableRow): string {
    return !this.isReferenceFeedAdvice &&
      !!this.tableScheme.usedReferenceFeedAdviceId &&
      Math.abs(
        tableRow.referenceSchemeIntestinalDigestibleLysinePerDay -
          tableRow.intestinalDigestibleLysinePerDay,
      ) /
        tableRow.intestinalDigestibleLysinePerDay >
        this.ALLOWED_LYSINE_DEVIATION
      ? 'highlight'
      : '';
  }

  checkValidation(tableRow: SchemeTableRow, type: string): string {
    return this.linesWithValidationErrors.some(
      (error) => error.line === tableRow.line && error.type === type,
    )
      ? 'validation'
      : '';
  }

  showLysine(tableRow: SchemeTableRow): string {
    const value = Math.round(tableRow.referenceSchemeIntestinalDigestibleLysinePerDay * 100) / 100;
    return !this.isReferenceFeedAdvice &&
      !!this.tableScheme.usedReferenceFeedAdviceId &&
      !isNaN(value)
      ? value.toLocaleString()
      : null;
  }

  getProductName(phase: Phase): string {
    const product = this.getProduct(phase.phaseNumber, <string>phase.product?.code);

    return product ? `${product.code}<br/>${product.name}` : '';
  }

  getProductDropdownValue(phasenumber: number) {
    if (!this.tableScheme || !this.phases?.[phasenumber - 1]?.product) {
      return null;
    }

    return this.getProduct(phasenumber, <string>this.phases[phasenumber - 1].product?.code);
  }

  getProduct(phasenumber: number, code: string): Product {
    let foundProduct = null;
    switch (phasenumber) {
      case 1:
        if (this.productsPhase1) {
          foundProduct = this.productsPhase1.find((product) => product.code === code);
        }
        break;
      case 2:
        if (this.productsPhase2) {
          foundProduct = this.productsPhase2.find((product) => product.code === code);
        }
        break;
      case 3:
        if (this.productsPhase3) {
          foundProduct = this.productsPhase3.find((product) => product.code === code);
        }
        break;
      case 4:
        if (this.productsPhase4) {
          foundProduct = this.productsPhase4.find((product) => product.code === code);
        }
        break;
    }
    return <Product>foundProduct;
  }

  setProduct(event: MatSelectChange, phase: Phase) {
    // update the product in the Phases variable
    this.phases = [
      ...this.phases.filter((p) => p.phaseNumber !== phase.phaseNumber),
      {
        ...this.phases.find((p) => p.phaseNumber === phase.phaseNumber),
        product: {
          ...(<Product>event.value),
        },
      },
    ].sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1));

    // recalculate the table
    this.updateTableRows();
  }

  changeNumberOfLines(operation: string) {
    this.changeNumberOfLinesClickStream$.next(operation);
  }
  recalculateNumberOfLines(operation: string) {
    const getNewTableRow = (row: SchemeTableRow): SchemeTableRow => {
      const newTableRow = <SchemeTableRow>{
        line: null,
        weight: null,
        correctionFactor: 1,
        energyValuePerDay: null,
        intestinalDigestibleLysinePerDay: null,
        consumptions: row.consumptions
          .map((c) => ({
            phaseNumber: c.phaseNumber,
          }))
          .sort((a, b) => (a.phaseNumber > b.phaseNumber ? 1 : -1)),
        foodPerDay: null,
        lysineValueFromReference: null,
        cumulative: null,
        cumulativeFoodInKg: null,
      };
      switch (this.feedAdviceType) {
        case FeedAdviceType.FATTENING:
          return <SchemeTableRow>{
            ...newTableRow,
            line: operation === 'add-top' ? row.line - 1 : row.line + 1,
            week: operation === 'add-top' ? row.week - 1 : row.week + 1,
            day: operation === 'add-top' ? row.day - 7 : row.day + 7,
          };
        case FeedAdviceType.REARING:
          return <SchemeTableRow>{
            ...newTableRow,
            line: operation === 'add-top' ? row.line - 1 : row.line + 1,
            week: operation === 'add-top' ? row.week - 1 : row.week + 1,
            weekAge: operation === 'add-top' ? row.weekAge : row.weekAge + 1,
            age: operation === 'add-top' ? row.age : row.age + 7,
          };
        case FeedAdviceType.WEANED_PIGLETS:
          return <SchemeTableRow>{
            ...newTableRow,
            line: operation === 'add-top' ? row.line - 1 : row.line + 1,
            day: operation === 'add-top' ? row.day - 1 : row.day + 1,
            age:
              operation === 'add-top'
                ? row.day - 1 + this.pigletsWeaningAge
                : row.day + 1 + this.pigletsWeaningAge,
          };
      }
    };
    const renumberLines = (tableRows: SchemeTableRow[], operation?: string): SchemeTableRow[] => {
      // renumber the lines in the ValidationErrors array ...
      this.linesWithValidationErrors = this.linesWithValidationErrors
        .map(
          (error) =>
            <ValidationError>{
              ...error,
              line: operation === 'add-top' ? error.line + 1 : error.line - 1,
            },
        )
        .filter((error) => error.line > 0);
      // ... as well as the lines in the tableRows.
      switch (this.feedAdviceType) {
        case FeedAdviceType.FATTENING:
          return tableRows.map((row, index) => ({
            ...row,
            line: index + 1,
            week: index + 1,
            day: (index + 1) * 7 - 6,
          }));
        case FeedAdviceType.REARING:
          if (operation === 'remove-top') {
            return tableRows.map((row, index) => ({
              ...row,
              line: index + 1,
              week: index + 1,
              weekAge: row.weekAge - 1,
              age: row.age - 7,
            }));
          } else {
            const age = tableRows[0].age;
            return tableRows.map((row, index) => ({
              ...row,
              line: index + 1,
              week: index + 1,
              weekAge:
                index === 0
                  ? row.weekAge
                  : Math.floor(
                      ((index + 1) * 7 - 6 + (this.rearingAge ? this.rearingAge - 1 : age - 1)) / 7,
                    ),
              age:
                index === 0
                  ? row.age
                  : (index + 1) * 7 - 6 + (this.rearingAge ? this.rearingAge - 1 : age - 1),
            }));
          }
        case FeedAdviceType.WEANED_PIGLETS:
          return tableRows.map((row, index) => ({
            ...row,
            line: index + 1,
            day: index + 1,
            age: index + 1 + (this.pigletsWeaningAge ? this.pigletsWeaningAge : 0),
          }));
      }
    };
    const addControlToFormArray = (index: number, row: SchemeTableRow) => {
      this.formArray.insert(index, this.createFormControl(row));
      this.formArray.controls[index].patchValue({
        lineNr: index + 1,
        weight: null,
        energyValue: null,
        correctionFactor: row.correctionFactor,
        cumulative: null,
        cumulativeFoodInKg: null,
        intestinalDigestibleLysinePerDay: null,
        foodPerDay: null,
        lysineValueFromReference: null,
        phase1: null,
        phase2: null,
        phase3: null,
        phase4: null,
        dosator_feeding_times: null,
        dosator_position_phase_1: null,
        dosator_position_phase_2: null,
        dosator_position_phase_3: null,
        dosator_position_phase_4: null,
      });
    };
    const renumberFormArray = () => {
      const formarrayvalue = [];
      for (let i = 1; i <= this.formArray.controls.length; i++) {
        const val = {
          ...this.formArray.controls[i - 1].value,
          lineNr: i,
          age:
            this.tableScheme.feedAdviceType === FeedAdviceType.REARING
              ? this.tableRows[i - 1].age
              : this.formArray.controls[i - 1].value.age,
        };
        this.formArray.controls[i - 1].patchValue(val, { onlySelf: true, emitEvent: false });
        formarrayvalue.push(val);
      }
      this.formArray.setValue(formarrayvalue, { onlySelf: true, emitEvent: false });
    };

    switch (operation) {
      case 'add-top':
        this.setTableRows(
          renumberLines(
            [getNewTableRow(<SchemeTableRow>this.tableRows[0]), ...this.tableRows].sort((a, b) =>
              a.line > b.line ? 1 : -1,
            ),
          ),
        );
        addControlToFormArray(0, this.tableRows[0]);
        renumberFormArray();
        this.addLinesWithValidationErrorsForAllTypes(this.tableRows[0].line);
        this.updateTableRows();
        break;

      case 'remove-top':
        this.setTableRows(
          renumberLines(
            this.tableRows.filter((row) => row.line !== this.tableRows[0].line),
            'remove-top',
          ),
        );
        this.formArray.removeAt(0);
        renumberFormArray();
        this.updateTableRows();
        break;

      case 'add-bottom': {
        const index = this.tableRows.length;
        const bottomRow = getNewTableRow(<SchemeTableRow>this.tableRows[index - 1]);
        this.setTableRows(
          [...this.tableRows, bottomRow].sort((a, b) => (a.line > b.line ? 1 : -1)),
        );
        addControlToFormArray(index, bottomRow);
        this.addLinesWithValidationErrorsForAllTypes(
          this.tableRows[this.tableRows.length - 1].line,
        );
        this.updateTableRows();
        break;
      }

      case 'remove-bottom': {
        const lastindex = this.tableRows.length - 1;
        this.setTableRows(
          this.tableRows.filter((row) => row.line !== this.tableRows[lastindex].line),
        );
        this.formArray.removeAt(lastindex);
        break;
      }
    }
    this.validateTable();

    // validationerrors for lines that are no longer in tableRows
    // must be removed, otherwise the data will not save.
    this.linesWithValidationErrors = this.linesWithValidationErrors.filter((lineWithError) =>
      this.tableRows.map((row) => row.line).includes(lineWithError.line),
    );
    this.refreshTableRows();
  }

  addLinesWithValidationErrorsForAllTypes(line: number) {
    const getInputSystemValidationType = () => {
      if (this.inputSystem === InputSystemType.ENERGYDAY) {
        return 'energyValue';
      }

      return 'foodPerDay';
    };

    this.linesWithValidationErrors.push(
      { line, type: 'weight' },
      { line, type: 'percentage' },
      {
        line,
        type: getInputSystemValidationType(),
      },
    );
  }

  isWeanedPiglets(): boolean {
    return this.feedAdviceType === FeedAdviceType.WEANED_PIGLETS;
  }

  isRearingGilt(): boolean {
    return this.feedAdviceType === FeedAdviceType.REARING;
  }

  isFattening(): boolean {
    return this.feedAdviceType === FeedAdviceType.FATTENING;
  }

  getTableAgeHeader(): string {
    const header = this.isRearingGilt() ? 'scheme-table.ageRearing' : 'scheme-table.age';
    return header;
  }

  shouldCalculateDosatorColumns(): boolean {
    return (
      this.isRearingFeedadvice() &&
      this.showDosatorColumn &&
      this.isEditMode &&
      this.selectedDosatorSettings !== undefined &&
      this.inputSystem === InputSystemType.KGDAY
    );
  }

  calculateDosatorColumns() {
    for (let tableRowIndex = 0; tableRowIndex < this.tableRows.length; tableRowIndex++) {
      const tableRow = this.tableRows[tableRowIndex];
      this.calculateColumnsValidationError = undefined;

      const consumptionResult = this.findConsumptionFullPercent(tableRow);
      const consumption = consumptionResult.consumption;
      if (!consumption) {
        this.calculateColumnsValidationError = consumptionResult.validationMessage;
        break;
      }

      const phaseNumber = consumption.phaseNumber;
      const phaseProductResult = this.findPhaseByNumber(phaseNumber);
      const phaseProduct = phaseProductResult.phase;
      if (!phaseProduct) {
        this.calculateColumnsValidationError = phaseProductResult.validationMessage;
        break;
      }

      const dosatorSettingsForProductResult = this.findDosatorSettingsForPhaseProduct(phaseProduct);
      const dosatorSettingsForProduct = dosatorSettingsForProductResult.dosatorSettings;
      if (!dosatorSettingsForProduct) {
        this.calculateColumnsValidationError = dosatorSettingsForProductResult.validationMessage;
        break;
      }

      const rowValueInLiters = consumption.dosatorPosition;
      const rowValueInKg = tableRow.foodPerDay;
      const rowValueFeedingTimesPerDay = tableRow.feedingTimesPerDay;

      if (rowValueInLiters) {
        // there is a value for the dosator
        // do the conversion from dosator/L to feed/Kg
        const calculatedFeedIntakeKg = this.calculateTargetValueBasedOnSourceValue(
          dosatorSettingsForProduct,
          rowValueInLiters,
          (c) => c.liter,
          (c) => c.kilogram,
          rowValueFeedingTimesPerDay,
          CalculationDirection.FromLitersToKg,
        );

        tableRow.foodPerDay = calculatedFeedIntakeKg;
        const formGroup = this.formArray.controls[tableRowIndex] as FormGroup;
        const formControl = formGroup.controls.foodPerDay;
        formControl.setValue(calculatedFeedIntakeKg);

        this.updateTableRows();
      } else if (rowValueInKg && !rowValueInLiters) {
        // there is a value for the feed and not a value for the dosator
        // do the conversion from feed/Kg to dosator/L
        const calculatedDosatorLiters = this.calculateTargetValueBasedOnSourceValue(
          dosatorSettingsForProduct,
          rowValueInKg,
          (c) => c.kilogram,
          (c) => c.liter,
          rowValueFeedingTimesPerDay,
          CalculationDirection.FromKgToLiters,
        );
        consumption.dosatorPosition = calculatedDosatorLiters;
        const formGroup = this.formArray.controls[tableRowIndex] as FormGroup;
        const formControl = formGroup.controls['dosator_position_phase_' + phaseNumber];
        formControl.setValue(calculatedDosatorLiters);

        this.updateTableRows();
      }
    }
  }

  findConsumptionFullPercent(tableRow: SchemeTableRow): CalculationConsumptionResult {
    const consumptionFullPercent = tableRow.consumptions.find(
      (c) => c.productIntakeInPercentage && c.productIntakeInPercentage === 100,
    );

    return {
      validationMessage: !consumptionFullPercent
        ? this.locale.translate('feed-advice-detail.validation-error.consumption-full-percent')
        : undefined,
      consumption: consumptionFullPercent,
    };
  }

  findPhaseByNumber(phaseNumber: number): CalculationPhaseResult {
    const phase = this.phases.find((phase) => phase.phaseNumber === phaseNumber);
    return {
      validationMessage: !phase
        ? this.locale.translate('feed-advice-detail.validation-error.product-not-in-phase')
        : undefined,
      phase,
    };
  }

  findDosatorSettingsForPhaseProduct(phaseProduct: Phase): CalculationDosatorSettingsResult {
    const dosatorSettings = this.selectedDosatorSettings.products.find(
      (p) => p.productId === phaseProduct.product?.id,
    );
    return {
      validationMessage: !dosatorSettings
        ? this.locale.translate(
            'feed-advice-detail.validation-error.dosator-settings-product-missing',
          ) + phaseProduct.product?.name
        : undefined,
      dosatorSettings,
    };
  }

  // Generic method to do the calculation from Liters to Kg or from Kg to Liters
  // dosatorSettingsForProduct - the Dosator settings selected in the dropdown
  // rowValueInLitersOrKg - the value entered in the Feed Intake (in Kg) or Dosator Position (in Liters)
  // sourceValueCallback - callback function to extract the source value from the conversion settings
  // targetValueCallback - callback function to extract the target value from the conversion settings
  // rowValueFeedingTimesPerDay - the value is used to multiply or divide the calculations result
  // calculationDirection - specify which direction we are calculating
  calculateTargetValueBasedOnSourceValue(
    dosatorSettingsForProduct: DosatorProduct,
    rowValueInLitersOrKg: number,
    sourceValueCallback: (conversion: DosatorConversions) => number,
    targetValueCallback: (conversion: DosatorConversions) => number,
    rowValueFeedingTimesPerDay: number,
    calculationDirection: CalculationDirection,
  ) {
    // detect the lower and higher conversions
    const sourceLowerAndHigherConversions = this.getDosatorSettingsLowerAndHigherConversions(
      dosatorSettingsForProduct,
      rowValueInLitersOrKg,
      (c) => sourceValueCallback(c),
    );
    const lowerConversion = sourceLowerAndHigherConversions.lowerConversion;
    const higherConversion = sourceLowerAndHigherConversions.higherConversion;

    if (!lowerConversion && !higherConversion) {
      return undefined;
    }

    if (lowerConversion === higherConversion) {
      // non-linear calculation
      // we pick up the matching conversion and apply same ratio to the source value
      let calculatedTargetValue =
        (targetValueCallback(lowerConversion) * rowValueInLitersOrKg) /
        sourceValueCallback(lowerConversion);

      calculatedTargetValue = this.adjustCalculatedValueWithFeedingTimesPerDay(
        calculatedTargetValue,
        calculationDirection,
        rowValueFeedingTimesPerDay,
      );
      return calculatedTargetValue;
    } else {
      // linear calculation
      const lowToHighConversionDif =
        sourceValueCallback(higherConversion) - sourceValueCallback(lowerConversion);
      const rowValueToLowConversionDif =
        rowValueInLitersOrKg - sourceValueCallback(lowerConversion);
      const rowValueToLowConversionPercentage =
        (rowValueToLowConversionDif * 100) / lowToHighConversionDif;

      const destLowToHighConversionDif =
        targetValueCallback(higherConversion) - targetValueCallback(lowerConversion);
      let calculatedTargetValue =
        (rowValueToLowConversionPercentage * destLowToHighConversionDif) / 100;
      calculatedTargetValue = targetValueCallback(lowerConversion) + calculatedTargetValue;

      calculatedTargetValue = this.adjustCalculatedValueWithFeedingTimesPerDay(
        calculatedTargetValue,
        calculationDirection,
        rowValueFeedingTimesPerDay,
      );
      return calculatedTargetValue;
    }
  }

  getDosatorSettingsLowerAndHigherConversions(
    dosatorSettingsProduct: DosatorProduct,
    valueInLitersOrKg: number,
    conversionLitersOrKgCallback: (conversion: DosatorConversions) => number,
  ): DosatorSettingsConversionLimits {
    const sortedConversions = dosatorSettingsProduct.conversions
      .slice()
      .sort((a, b) => conversionLitersOrKgCallback(a) - conversionLitersOrKgCallback(b));

    let lowerConversion: DosatorConversions;
    let higherConversion: DosatorConversions;

    if (sortedConversions.length === 0) {
      return {
        higherConversion,
        lowerConversion,
      };
    }

    if (sortedConversions.length === 1) {
      return {
        lowerConversion: sortedConversions[0],
        higherConversion: sortedConversions[0],
      };
    }

    for (let i = 0; i <= sortedConversions.length - 2; i++) {
      lowerConversion = sortedConversions[i];
      higherConversion = sortedConversions[i + 1];

      const lowerConversionValue = conversionLitersOrKgCallback(lowerConversion);
      const higherConversionValue = conversionLitersOrKgCallback(higherConversion);

      const isLowerThanFirstConversion = i === 0 && valueInLitersOrKg <= lowerConversionValue;
      const isHigherThanLastConversion =
        i === sortedConversions.length - 2 && valueInLitersOrKg >= higherConversionValue;

      if (isLowerThanFirstConversion || valueInLitersOrKg === lowerConversionValue) {
        return {
          lowerConversion,
          higherConversion: lowerConversion,
        };
      }

      if (isHigherThanLastConversion || valueInLitersOrKg === higherConversionValue) {
        return {
          lowerConversion: higherConversion,
          higherConversion,
        };
      }

      if (valueInLitersOrKg >= lowerConversionValue && valueInLitersOrKg < higherConversionValue) {
        return {
          lowerConversion,
          higherConversion,
        };
      }
    }

    return {
      higherConversion: undefined,
      lowerConversion: undefined,
    };
  }

  adjustCalculatedValueWithFeedingTimesPerDay(
    calculatedValue: number,
    calculationDirection: CalculationDirection,
    feedingTimesPerDay?: number,
  ): number {
    if (feedingTimesPerDay) {
      if (calculationDirection === CalculationDirection.FromLitersToKg) {
        return calculatedValue * feedingTimesPerDay;
      } else if (calculationDirection === CalculationDirection.FromKgToLiters) {
        return calculatedValue / feedingTimesPerDay;
      }
    }

    return calculatedValue;
  }
}
